import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Wifi, RefreshCw, Trash2 } from 'lucide-react'
import {
    getBsnlConnections, createBsnlConnection, deleteBsnlConnection,
    getBsnlRestorations, createBsnlRestoration,
} from '../../services/staffServices'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { StaggerContainer, StaggerItem, ModalOverlay, ButtonLoader } from './components/StaffAnimations'

// bsnl_connections: id(text,required), name, phone_number, email, address,
//   tariff_plan, connection_mode(Paid|Free), connection_type(Public|Private),
//   fiber_required_meters, lead_generated_by, lead_generated_by_id
function ConnModal({ profile, onClose, onSave }) {
    const [form, setForm] = useState({
        id: '', name: '', phone_number: '', email: '', address: '',
        tariff_plan: '', connection_mode: 'Paid', connection_type: 'Public',
        fiber_required_meters: 0,
    })
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900">
                    <h2 className="font-semibold text-white">New BSNL Connection</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <form onSubmit={e => {
                    e.preventDefault()
                    if (!form.id || !form.name || !form.phone_number) { toast.error('ID, Name & Phone required'); return }
                    onSave({ ...form, fiber_required_meters: parseFloat(form.fiber_required_meters) || 0, lead_generated_by: profile?.name || '', lead_generated_by_id: profile?.id || '' })
                }} className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Connection ID *</label>
                            <input value={form.id} onChange={e => set('id', e.target.value)}
                                placeholder="e.g. BSNL-2024-001"
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Customer Name *</label>
                            <input value={form.name} onChange={e => set('name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Phone *</label>
                            <input value={form.phone_number} onChange={e => set('phone_number', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Email</label>
                            <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Address</label>
                            <textarea value={form.address} onChange={e => set('address', e.target.value)} rows={2}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Tariff Plan</label>
                            <input value={form.tariff_plan} onChange={e => set('tariff_plan', e.target.value)}
                                placeholder="e.g. BBG COMBO UL 499"
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Fiber Required (m)</label>
                            <input type="number" min="0" value={form.fiber_required_meters} onChange={e => set('fiber_required_meters', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Mode</label>
                            <select value={form.connection_mode} onChange={e => set('connection_mode', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>Paid</option><option>Free</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Type</label>
                            <select value={form.connection_type} onChange={e => set('connection_type', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>Public</option><option>Private</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">Cancel</button>
                        <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition">Add Connection</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

// bsnl_restorations: landline_account_number, connection_type(Fiber/Broadband/Landline),
//   reason_for_disconnection, name, phone_number, address, parts_required,
//   outstanding_amount, payment_mode(Cash|Online|Cheque), additional_notes,
//   created_by, created_by_id
function RestModal({ profile, onClose, onSave }) {
    const [form, setForm] = useState({
        landline_account_number: '', connection_type: 'Fiber (FTTH)',
        reason_for_disconnection: 'Other', name: '', phone_number: '',
        address: '', parts_required: '', outstanding_amount: '',
        payment_mode: 'Cash', additional_notes: '',
        damaged_item_photo_url: '', restored_item_photo_url: '',
    })
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900">
                    <h2 className="font-semibold text-white">New Restoration</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <form onSubmit={e => {
                    e.preventDefault()
                    if (!form.name || !form.phone_number || !form.landline_account_number) { toast.error('Account No, Name & Phone required'); return }
                    onSave({ ...form, created_by: profile?.name || '', created_by_id: profile?.id || '' })
                }} className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Landline / Account No. *</label>
                            <input value={form.landline_account_number} onChange={e => set('landline_account_number', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Customer Name *</label>
                            <input value={form.name} onChange={e => set('name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Phone *</label>
                            <input value={form.phone_number} onChange={e => set('phone_number', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Connection Type</label>
                            <select value={form.connection_type} onChange={e => set('connection_type', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>Fiber (FTTH)</option><option>Broadband (DSL)</option><option>Landline</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Reason</label>
                            <select value={form.reason_for_disconnection} onChange={e => set('reason_for_disconnection', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>Non-payment</option><option>Customer Request</option><option>Technical Issue</option><option>Other</option>
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Address</label>
                            <textarea value={form.address} onChange={e => set('address', e.target.value)} rows={2}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Parts Required</label>
                            <input value={form.parts_required} onChange={e => set('parts_required', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Outstanding Amount</label>
                            <input value={form.outstanding_amount} onChange={e => set('outstanding_amount', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Payment Mode</label>
                            <select value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>Cash</option><option>Online</option><option>Cheque</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Additional Notes</label>
                            <input value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">Cancel</button>
                        <button type="submit" className="flex-1 bg-green-600 hover:bg-green-500 text-white py-2.5 rounded-xl text-sm font-medium transition">Add Restoration</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default function BSNL() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()
    const [tab, setTab] = useState('connections')
    const [modal, setModal] = useState(null)

    const { data: connections = [], isLoading: loadConn } = useQuery({ queryKey: ['bsnl-connections'], queryFn: getBsnlConnections })
    const { data: restorations = [], isLoading: loadRest } = useQuery({ queryKey: ['bsnl-restorations'], queryFn: getBsnlRestorations })

    const addConn = useMutation({ mutationFn: createBsnlConnection, onSuccess: () => { qc.invalidateQueries(['bsnl-connections']); setModal(null); toast.success('Connection added') }, onError: e => toast.error(e.message) })
    const addRest = useMutation({ mutationFn: createBsnlRestoration, onSuccess: () => { qc.invalidateQueries(['bsnl-restorations']); setModal(null); toast.success('Restoration added') }, onError: e => toast.error(e.message) })
    const delConn = useMutation({ mutationFn: deleteBsnlConnection, onSuccess: () => { qc.invalidateQueries(['bsnl-connections']); toast.success('Deleted') }, onError: e => toast.error(e.message) })

    const data = tab === 'connections' ? connections : restorations
    const loading = tab === 'connections' ? loadConn : loadRest

    return (
        <StaffLayout>
            <div className="space-y-5">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">BSNL</h1>
                    <button onClick={() => setModal(tab)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
                        <Plus size={16} /> Add {tab === 'connections' ? 'Connection' : 'Restoration'}
                    </button>
                </div>

                <div className="flex gap-2">
                    {[['connections', Wifi, `Connections (${connections.length})`], ['restorations', RefreshCw, `Restorations (${restorations.length})`]].map(([key, Icon, label]) => (
                        <button key={key} onClick={() => setTab(key)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition ${tab === key ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white border border-white/5'}`}>
                            <Icon size={15} /> {label}
                        </button>
                    ))}
                </div>

                <div className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden">
                    {loading ? <div className="p-8 text-center text-slate-400">Loading...</div> :
                        data.length === 0 ? <div className="p-12 text-center text-slate-500">No records found</div> : (
                            <StaggerContainer className="divide-y divide-white/5">
                                {data.map(item => (
                                    <StaggerItem key={item.id}>
                                    <div className="px-5 py-4 hover:bg-white/2 transition">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="font-medium text-white">{item.name}</p>
                                                <p className="text-sm text-slate-400">{item.phone_number}</p>
                                                {tab === 'connections' && (
                                                    <>
                                                        {item.tariff_plan && <p className="text-xs text-slate-500 mt-1">Plan: {item.tariff_plan}</p>}
                                                        <div className="flex gap-2 mt-1">
                                                            <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{item.connection_mode}</span>
                                                            <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{item.connection_type}</span>
                                                            {item.fiber_required_meters > 0 && <span className="text-xs text-blue-400">{item.fiber_required_meters}m fiber</span>}
                                                        </div>
                                                    </>
                                                )}
                                                {tab === 'restorations' && (
                                                    <>
                                                        <p className="text-xs text-slate-500 mt-1">Account: {item.landline_account_number}</p>
                                                        <p className="text-xs text-slate-500">Reason: {item.reason_for_disconnection}</p>
                                                        {item.outstanding_amount && <p className="text-xs text-amber-400">Outstanding: ₹{item.outstanding_amount}</p>}
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs text-slate-500">{new Date(item.created_at).toLocaleDateString('en-IN')}</span>
                                                {tab === 'connections' && (
                                                    <button onClick={() => { if (window.confirm('Delete?')) delConn.mutate(item.id) }} className="text-slate-400 hover:text-red-400 transition">
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    </StaggerItem>
                                ))}
                            </StaggerContainer>
                        )}
                </div>
            </div>
            <ModalOverlay show={modal === 'connections'}>
                {modal === 'connections' && <ConnModal profile={profile} onClose={() => setModal(null)} onSave={d => addConn.mutate(d)} />}
            </ModalOverlay>
            <ModalOverlay show={modal === 'restorations'}>
                {modal === 'restorations' && <RestModal profile={profile} onClose={() => setModal(null)} onSave={d => addRest.mutate(d)} />}
            </ModalOverlay>
        </StaffLayout>
    )
}
